/**
 * StackItem — a single PropStack row in the inventory or container list.
 * Shows icon placeholder, name, quantity, quality badge, and a hover
 * tooltip with weight-per-unit, quality multiplier, freshness (M3.3a C2).
 */

import { useState } from 'react';

import { propLabel } from '../data/prop-catalog';
import type { PropStackWire } from '../wire';
import { QualityBadge } from './QualityBadge';
import { QUALITY_MULTIPLIERS } from './quality';
import styles from './inventory.module.css';

export interface StackItemProps {
  stack: PropStackWire;
  /** Weight per unit from the prop catalog. 0 if unknown. */
  weightPerUnit?: number;
  /** Additional click handler (e.g. drag-start, equip). */
  onClick?: () => void;
}

export function StackItem({ stack, weightPerUnit = 0, onClick }: StackItemProps): JSX.Element {
  const [hovered, setHovered] = useState(false);
  const name = propLabel(stack.prop_id);
  const multiplier = QUALITY_MULTIPLIERS[stack.quality];
  const freshLabel = stack.freshness !== null ? 'Perishable' : null;

  return (
    <li
      className={styles.stackItem}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={onClick}
      data-testid="stack-item"
    >
      <div className={styles.stackIcon}>
        <QualityBadge quality={stack.quality} />
      </div>
      <div className={styles.stackInfo}>
        <div className={styles.stackName}>{name}</div>
        <div className={styles.stackMeta}>
          <span>{stack.quality}</span>
          {stack.state && 'Liquid' in stack.state ? (
            <span>Liquid</span>
          ) : null}
          {freshLabel ? <span>{freshLabel}</span> : null}
        </div>
      </div>
      <span className={styles.stackQty}>x{stack.qty}</span>
      {hovered ? (
        <div className={styles.tooltip}>
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipLabel}>Weight/unit</span>
            <span className={styles.tooltipValue}>
              {weightPerUnit > 0 ? weightPerUnit.toFixed(2) : '?'}
            </span>
          </div>
          <div className={styles.tooltipRow}>
            <span className={styles.tooltipLabel}>Quality mult.</span>
            <span className={styles.tooltipValue}>x{multiplier.toFixed(1)}</span>
          </div>
          {freshLabel ? (
            <div className={styles.tooltipRow}>
              <span className={styles.tooltipLabel}>Freshness</span>
              <span className={styles.tooltipValue}>{freshLabel}</span>
            </div>
          ) : null}
        </div>
      ) : null}
    </li>
  );
}
